import { React, useState, useContext, useEffect, Fragment } from "react";
import { Container, Row, Col, Card, Form } from "react-bootstrap";
import { Pie, Line } from "react-chartjs-2";
import axios from "axios";
import { Authorization } from "../../../Context/Authorization";
import Preloader from "../../../shared/PreLoaders";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
} from "chart.js";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title
);
const SHGProfile = () => {
  const [ranges, setRanges] = useState([]);
  const [vfcData, setVfcData] = useState([]);
  const [shgData, setShgData] = useState([]);
  const [selectedRange, setSelectedRange] = useState("");
  const [selectedVfc, setSelectedVfc] = useState("");
  const [selectedShg, setSelectedShg] = useState("");
  const [loading, setLoading] = useState(true);
  const Api = process.env.REACT_APP_API;

  useEffect(() => {
    // Fetch available ranges
    setLoading(true);
    const fetchRanges = async () => {
      try {
        const config = Authorization();
        const response = await axios.get(`${Api}/api/ranges`, config);
        const data = response.data;
        setRanges(data);
      } catch (err) {
        console.error("Error fetching ranges data", err);
      }
      setLoading(false);
    };

    fetchRanges();
  }, [Api]);

  const handleRangeChange = async (e) => {
    const rangeId = e.target.value;
    setLoading(true);
    setSelectedRange(rangeId);
    setSelectedVfc("");
    setSelectedShg("");
    try {
      const config = Authorization();
      const response = await axios.post(
        `${Api}/api/vfcs/vfcs-by-range`,
        {
          rangeId,
        },
        config
      );
      const data = response.data.vfcs;
      setVfcData(data);
    } catch (error) {
      console.log("Error fetching VFCs", error);
    }
    setLoading(false);
  };

  const handleVfcChange = async (e) => {
    setLoading(true);
    const vfcId = e.target.value;
    setSelectedVfc(vfcId);
    setSelectedShg("");
    try {
      const config = Authorization();
      const response = await axios.post(
        `${Api}/api/shgs/getShgsByVFC`,
        { vfcId },
        config
      );
      console.log("API Response:", response.data);

      // Ensure shgs is an array before setting it
      if (Array.isArray(response.data.shgs)) {
        setShgData(response.data.shgs);
      } else {
        setShgData([]); // Fallback to an empty array if the response is not an array
        console.error("SHGs data is not an array:", response.data);
      }
    } catch (error) {
      console.error("Error fetching SHGs:", error);
      setShgData([]); // Fallback in case of error
    }
    setLoading(false);
  };

  const handleShgChange = (e) => {
    const shgId = e.target.value;
    setSelectedShg(shgId);
  };

  // Dummy data for PieChart (SHG Activities)
  const dummyActivities = [
    { activity: "Agriculture", beneficiaries: 40 },
    { activity: "Tailoring", beneficiaries: 30 },
    { activity: "Handicrafts", beneficiaries: 20 },
    { activity: "Animal Husbandry", beneficiaries: 10 },
  ];

  // Dummy data for LineChart (Loan Repayment History)
  const dummyRepaymentHistory = [
    { month: "Jan", amount: 1000 },
    { month: "Feb", amount: 1500 },
    { month: "Mar", amount: 2000 },
    { month: "Apr", amount: 1800 },
  ];

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div>
      <Preloader loading={loading} />
      <div className="page-header">
        <h3 className="page-title"> SHG Profile </h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="!#" onClick={(event) => event.preventDefault()}>
                VFC
              </a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              SHG Profile
            </li>
          </ol>
        </nav>
      </div>

      <Container className="my-5">
        <Fragment>
          <div className="col-md-12 grid-margin stretch-card">
            <div className="card">
              <div className="card-body">
                <form>
                  <Row>
                    <Col md={4} className="mt-3">
                      <Form.Group controlId="formRangeSelect">
                        <Form.Label>Select Range</Form.Label>

                        <Form.Control
                          as="select"
                          value={selectedRange}
                          onChange={handleRangeChange}
                        >
                          <option value="">Select Range</option>
                          {ranges.map((range) => (
                            <option key={range._id} value={range._id}>
                              {range.rangeName}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group controlId="formVfcSelect" className="mt-3">
                        <Form.Label>Select VFC</Form.Label>

                        <Form.Control
                          as="select"
                          value={selectedVfc}
                          onChange={handleVfcChange}
                        >
                          <option value="">Select VFC</option>
                          {vfcData.map((vfc) => (
                            <option key={vfc._id} value={vfc._id}>
                              {vfc.vfcName}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group controlId="formShgSelect" className="mt-3">
                        <Form.Label>Select SHG</Form.Label>

                        <Form.Control
                          as="select"
                          value={selectedShg}
                          onChange={handleShgChange}
                          disabled={!shgData || shgData.length === 0}
                        >
                          <option value="">Select SHG</option>
                          {shgData.map((shg) => (
                            <option key={shg._id} value={shg._id}>
                              {shg.name}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>
                </form>
              </div>
            </div>
          </div>

          <Row className="mt-4">
            <Col md={4}>
              {selectedShg && (
                <Card className="mb-3">
                  <Card.Body>
                    <h3 className="mt-3">
                      {shgData.find((shg) => shg._id === selectedShg)?.name ||
                        "SHG Name"}
                    </h3>

                    <p>
                      <strong>VFC:</strong>{" "}
                      {
                        shgData.find((shg) => shg._id === selectedShg)?.vfcId
                          .vfcName
                      }
                    </p>
                    <p>
                      <strong>Code:</strong>{" "}
                      {shgData.find((shg) => shg._id === selectedShg)?.code}
                    </p>
                    <p>
                      <strong>Type:</strong>{" "}
                      {shgData.find((shg) => shg._id === selectedShg)?.type}
                    </p>
                    <p>
                      <strong>Location:</strong>{" "}
                      {shgData.find((shg) => shg._id === selectedShg)?.location}
                    </p>
                    <p>
                      <strong>Date of Establishment:</strong>{" "}
                      {
                        shgData.find((shg) => shg._id === selectedShg)
                          ?.dateOfEstablishment
                      }
                    </p>
                    <p>
                      <strong>Bank Name:</strong>{" "}
                      {shgData.find((shg) => shg._id === selectedShg)?.bankName}
                    </p>
                    <p>
                      <strong>Branch:</strong>{" "}
                      {shgData.find((shg) => shg._id === selectedShg)?.branch}
                    </p>
                    <p>
                      <strong>Account Number:</strong>{" "}
                      {
                        shgData.find((shg) => shg._id === selectedShg)
                          ?.accountNumber
                      }
                    </p>
                    <p>
                      <strong>Address:</strong>{" "}
                      {shgData.find((shg) => shg._id === selectedShg)?.address}
                    </p>
                    <p>
                      <strong>PinCode:</strong>{" "}
                      {shgData.find((shg) => shg._id === selectedShg)?.pinCode}
                    </p>
                  </Card.Body>
                </Card>
              )}
            </Col>

            {/* <Col md={8}>
              {selectedShg && (
                <Card>
                  <Card.Header>SHG Activities</Card.Header>
                  <Card.Body>
                    <Pie data={dummyActivities} />
                  </Card.Body>
                </Card>
              )}
            </Col> */}
          </Row>

          {/* Loan Repayment History */}
          {/* {selectedShg && (
            <Row className="mt-4">
              <Col md={12}>
                <Card>
                  <Card.Header>Loan Repayment History</Card.Header>
                  <Card.Body>
                    <Line data={dummyRepaymentHistory} />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )} */}
        </Fragment>
      </Container>
    </div>
  );
};

export default SHGProfile;
